/**
 * Universal SEO, Canonical, Meta Tags, and Custom Scripts Helper
 * Ensures 100% compliant SEO, dynamic canonical URLs, Open Graph, Twitter Cards,
 * and dynamic JSON-LD Schema / Custom Scripts (GTM, Schema, Analytics).
 */

const SITE_DOMAIN = "https://vivashri.com";
const DEFAULT_LOGO = "https://vivashri.com/assets/img/logo-light.png";

/**
 * Clean canonical URL builder (standardizes domain, trims trailing slashes unless root)
 */
export const getCleanCanonicalUrl = (customUrl = null) => {
  if (customUrl && typeof customUrl === "string" && customUrl.trim()) {
    let clean = customUrl.trim();
    if (clean.startsWith("http://") || clean.startsWith("https://")) {
      return clean.replace(/\/+$/, "") || SITE_DOMAIN;
    }
    const path = clean.startsWith("/") ? clean : `/${clean}`;
    return `${SITE_DOMAIN}${path.replace(/\/+$/, "") || ""}`;
  }

  if (typeof window !== "undefined") {
    const pathname = window.location.pathname.replace(/\/+$/, "");
    return `${SITE_DOMAIN}${pathname || "/"}`;
  }

  return SITE_DOMAIN;
};

/**
 * Set full page SEO attributes: Title, Description, Keywords, Canonical, OG & Twitter Meta
 */
export const setPageSEO = (
  title,
  description,
  keywords,
  canonicalUrl = null,
  ogImage = DEFAULT_LOGO,
  ogType = "website"
) => {
  if (typeof document === "undefined") return;

  // 1. Page Title
  if (title) {
    document.title = title;
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute("content", title);

    const twitterTitle =
      document.querySelector('meta[name="twitter:title"]') ||
      document.querySelector('meta[name="X:title"]');
    if (twitterTitle) twitterTitle.setAttribute("content", title);
  }

  // 2. Meta Description
  if (description) {
    const metaDescs = document.querySelectorAll('meta[name="description"]');
    if (metaDescs.length === 0) {
      const metaDesc = document.createElement("meta");
      metaDesc.name = "description";
      metaDesc.content = description;
      document.head.appendChild(metaDesc);
    } else {
      metaDescs.forEach((el) => el.setAttribute("content", description));
    }

    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute("content", description);

    const twitterDesc =
      document.querySelector('meta[name="twitter:description"]') ||
      document.querySelector('meta[name="X:description"]');
    if (twitterDesc) twitterDesc.setAttribute("content", description);
  }

  // 3. Meta Keywords
  if (keywords) {
    let metaKw = document.querySelector('meta[name="keywords"]');
    if (!metaKw) {
      metaKw = document.createElement("meta");
      metaKw.name = "keywords";
      document.head.appendChild(metaKw);
    }
    metaKw.setAttribute("content", keywords);
  }

  // 4. Dynamic Canonical Tag & OpenGraph URL (Always updated accurately)
  const finalCanonical = getCleanCanonicalUrl(canonicalUrl);

  let canonicalTag = document.querySelector('link[rel="canonical"]');
  if (!canonicalTag) {
    canonicalTag = document.createElement("link");
    canonicalTag.rel = "canonical";
    document.head.appendChild(canonicalTag);
  }
  canonicalTag.setAttribute("href", finalCanonical);

  const ogUrl = document.querySelector('meta[property="og:url"]');
  if (ogUrl) {
    ogUrl.setAttribute("content", finalCanonical);
  }

  // 5. OpenGraph & Twitter Image
  if (ogImage) {
    const finalImage =
      ogImage.startsWith("http://") || ogImage.startsWith("https://")
        ? ogImage
        : `${SITE_DOMAIN}/${ogImage.replace(/^\/+/, "")}`;

    let ogImgTag = document.querySelector('meta[property="og:image"]');
    if (ogImgTag) ogImgTag.setAttribute("content", finalImage);

    let twitterImgTag =
      document.querySelector('meta[name="twitter:image"]') ||
      document.querySelector('meta[name="X:image"]');
    if (twitterImgTag) twitterImgTag.setAttribute("content", finalImage);
  }

  // 6. OpenGraph Type
  if (ogType) {
    let ogTypeTag = document.querySelector('meta[property="og:type"]');
    if (ogTypeTag) ogTypeTag.setAttribute("content", ogType);
  }
};

/**
 * Inject Custom Head/Body Scripts (GTM, Analytics, Custom Schemas, Pixels)
 * Safely handles execution and prevents duplicate / stale tags when switching pages
 */
export const injectCustomScript = (customScriptString, containerId = "custom-page-scripts-container") => {
  if (typeof document === "undefined") return () => {};

  // Clean up any existing container first
  const existing = document.getElementById(containerId);
  if (existing) {
    existing.remove();
  }

  if (
    !customScriptString ||
    typeof customScriptString !== "string" ||
    !customScriptString.trim()
  ) {
    return () => {};
  }

  try {
    const container = document.createElement("div");
    container.id = containerId;
    container.style.display = "none";

    const parser = new DOMParser();
    const doc = parser.parseFromString(customScriptString, "text/html");
    const scripts = doc.querySelectorAll("script");

    if (scripts.length > 0) {
      scripts.forEach((oldScript) => {
        const newScript = document.createElement("script");
        Array.from(oldScript.attributes).forEach((attr) => {
          newScript.setAttribute(attr.name, attr.value);
        });
        newScript.textContent = oldScript.textContent;
        container.appendChild(newScript);
      });
    } else {
      const newScript = document.createElement("script");
      newScript.textContent = customScriptString;
      container.appendChild(newScript);
    }

    Array.from(doc.body.children).forEach((el) => {
      if (el.tagName.toLowerCase() !== "script") {
        container.appendChild(el.cloneNode(true));
      }
    });

    document.head.appendChild(container);
  } catch (err) {
    console.error("injectCustomScript error:", err);
  }

  // Cleanup function for useEffect return
  return () => {
    const el = document.getElementById(containerId);
    if (el) el.remove();
  };
};

/**
 * Inject JSON-LD Structured Data Schema into document head
 */
export const injectJsonLd = (schemaObj, scriptId = "jsonld-page-schema") => {
  if (typeof document === "undefined" || !schemaObj) return () => {};

  const existing = document.getElementById(scriptId);
  if (existing) {
    existing.remove();
  }

  try {
    const script = document.createElement("script");
    script.id = scriptId;
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(schemaObj, null, 2);
    document.head.appendChild(script);
  } catch (err) {
    console.error("injectJsonLd error:", err);
  }

  return () => {
    const el = document.getElementById(scriptId);
    if (el) el.remove();
  };
};

export default {
  setPageSEO,
  getCleanCanonicalUrl,
  injectCustomScript,
  injectJsonLd,
};
